import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header";

// Define the image type
interface ImageData {
  src: string;
  category: string;
}

// Dynamically load images from subfolders
const importAllImages = (): ImageData[] => {
  const context = require.context("../assets/img/projects", true, /\.(png|jpe?g|svg)$/);
  return context.keys().map((key) => {
    const category = key.split("/")[1]; // Extract folder name as the category
    return { src: context(key), category };
  });
};

// Dynamically loaded images
const images: ImageData[] = importAllImages();

// Generate categories
const categories: string[] = ["All", ...new Set(images.map((image) => image.category))];

const GalleryPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  // Filter images based on the selected category
  const filteredImages = selectedCategory === "All"
    ? images
    : images.filter((image) => image.category === selectedCategory);

  return (
    <>
      <Header />
      <GalleryContainer>
        <Content>
          <Title>Our Work</Title>
          <FilterBar>
            {categories.map((category) => (
              <FilterButton
                key={category}
                active={category === selectedCategory}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </FilterButton>
            ))}
          </FilterBar>
          <Grid>
            <AnimatePresence>
              {filteredImages.map((image, index) => (
                <ImageWrapper
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Image src={image.src} alt={`Project ${index + 1}`} />
                </ImageWrapper>
              ))}
            </AnimatePresence>
          </Grid>
        </Content>
      </GalleryContainer>
    </>
  );
};

// Styled Components
const GalleryContainer = styled.div`
  padding: 80px 20px;
  background-color: #f3f6fb;
`;

const Content = styled.div`
  width: 80%;
  margin: auto; /* Center content horizontally */
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 40px;
  font-weight: bold;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  background-color: ${({ active }) => (active ? "#ffa500" : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#003366")};
  border: 1px solid #003366;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: #ffa500;
    color: #fff;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageWrapper = styled(motion.div)`
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  &:hover img {
    transform: scale(1.1);
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

export default GalleryPage;
